//https://unicode-org.github.io/icu/userguide/format_parse/datetime/#datetime-format-syntax
import { DateObjectUnits, DateTime, DateTimeFormatOptions, DurationUnits } from 'luxon'

export type DateType = DateTime

const defaultFormat = 'yyyy-MM-dd'

export function today() {
   return DateTime.now()
}

export function createDate(date?: string | Date | Array<number>, format = defaultFormat) {
   if (!date || !isDate(date, format)) {
      return null
   }

   if (typeof date === 'string') {
      return DateTime.fromFormat(date, format)
   } else if (Array.isArray(date)) {
      if (date.length >= 3) {
         return DateTime.local(date[0], date[1], date[2])
      }
   } else {
      return DateTime.fromJSDate(date)
   }
   return null
}

export function isDate(date?: string | Date | Array<number> | null, format = defaultFormat) {
   if (!date) {
      return null
   }
   if (typeof date === 'string') {
      return DateTime.fromFormat(date, format).isValid
   } else if (Array.isArray(date)) {
      if (date.length >= 3) {
         return DateTime.local(date[0], date[1], date[2]).isValid
      }
   } else {
      return DateTime.fromJSDate(date).isValid
   }
   return null
}

export function getNights(startDate?: string, endDate?: string, format = defaultFormat) {
   const checkIn = createDate(startDate, format)
   const checkOut = createDate(endDate, format)
   if (checkIn && checkOut) {
      return Math.abs(checkIn.diff(checkOut, 'days').days)
   } else {
      return 0
   }
}

export function getFormattedDate(date: string | DateTime | Array<number>, format: string | DateTimeFormatOptions, locale: string, inputFormat: string = defaultFormat): string | null {
   let result: DateTime | null = null;
   if (typeof date === 'string' || Array.isArray(date)) {
      result = createDate(date, inputFormat)
   } else {
      result = date
   }
   if (result) {
      if (typeof format === 'string') {
         return result.setLocale(locale).toFormat(format)
      } else {
         return result.setLocale(locale).toLocaleString(format)
      }
   }

   return null
}

export function getMinDate(dates: Array<DateTime | string>) {
   const filteredDates = dates.map(d => typeof d === 'string' ? createDate(d) : d).filter(s => s) as Array<DateTime>
   return DateTime.min(...filteredDates);
}

export function getMaxDate(dates: Array<DateTime | string>) {
   const filteredDates = dates.map(d => typeof d === 'string' ? createDate(d) : d).filter(s => s) as Array<DateTime>
   return DateTime.max(...filteredDates);
}

export function createDateFromISOAndZone(date: string) {
   return DateTime.fromISO(date)
}

export function createDateFromISO(date: string, options?: { setZone: boolean }) {
   return DateTime.fromISO(date, options ? { ...options.setZone && { setZone: options.setZone } } : undefined)
}

// temp_: el backend corre en Cancún (America/Cancun = EST, UTC-5 fijo año redondo
// sin DST) y retorna hora local sin "Z". Parsear con America/Cancun da el instante
// UTC correcto; el clamp corrige drift pequeño del reloj del servidor (< 5 min) para
// que una acción/evento recién creado (nota, operación, actividad) no se vea "en el futuro".
// Eliminar cuando el backend retorne UTC con Z.
export function temp_createEventDateFromISO(date: string, zone='America/Cancun') {
   const eventTime = DateTime.fromISO(date, { zone })
   const now = DateTime.now()
   const diffMinutes = eventTime.diff(now, 'minutes').minutes
   return diffMinutes > 0 && diffMinutes < 5 ? now.minus({ seconds: 1 }) : eventTime
}

export function createDateFromObject(object: DateObjectUnits) {
   return DateTime.fromObject(object)
}

export function getDiff(date1: string, date2: string, unit: DurationUnits) {
   const objDate1 = createDate(date1)
   const objDate2 = createDate(date2)
   if (objDate1 && objDate2) {
      return objDate1.diff(objDate2, unit).toObject()
   }
   return null
}

export type FreeCancellationLimit = {
   date: DateTime
   /** null cuando el texto es solo "antes del {date}" */
   time: string | null
}

const SAME_DAY_TIME = '12:00 pm'

/**
 * Listados de habitaciones: "antes del {día siguiente al último día gratis}", sin hora.
 * Si es gratis hasta el día de llegada, el día siguiente ya sería con el huésped hospedado: se usa la llegada a las 12:00 pm.
 */
export function getListingFreeCancellationLimit(
   arrival: string,
   daysBeforeArrival: number,
): FreeCancellationLimit | null {
   const arrivalDate = createDate(arrival)
   if (!arrivalDate) {
      return null
   }
   if (daysBeforeArrival === 0) {
      return { date: arrivalDate, time: SAME_DAY_TIME }
   }
   return { date: arrivalDate.minus({ day: daysBeforeArrival }).plus({ day: 1 }), time: null }
}

/** Checkout: "antes del {último día gratis} a las 11:59 pm" (12:00 pm si es el día de llegada). */
export function getCheckoutFreeCancellationLimit(
   arrival: string,
   daysBeforeArrival: number,
): FreeCancellationLimit | null {
   const arrivalDate = createDate(arrival)
   if (!arrivalDate) {
      return null
   }
   return {
      date: arrivalDate.minus({ day: daysBeforeArrival }),
      time: daysBeforeArrival === 0 ? SAME_DAY_TIME : '11:59 pm',
   }
}
